import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { profileApi, Bank } from '../../api/earnings';

interface Props {
  onClose: () => void;
  onSaved: () => void;
}

export const PayoutBankModal: React.FC<Props> = ({ onClose, onSaved }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [bankCode, setBankCode] = useState('');
  // Searchable bank picker: free-text query + open/close for the dropdown.
  const [bankSearch, setBankSearch] = useState('');
  const [bankMenuOpen, setBankMenuOpen] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [profile, bankList] = await Promise.all([
          profileApi.get(),
          profileApi.getBanks(),
        ]);
        setBanks(bankList);
        setBankCode(profile.payoutBankCode ?? '');
        // Seed the search box with the saved bank's name so it reads clearly.
        const savedBank = bankList.find(
          (b) => b.bankCode === profile.payoutBankCode,
        );
        setBankSearch(savedBank?.bankName ?? '');
        setAccountNumber(profile.payoutAccountNumber ?? '');
        setAccountName(profile.payoutAccountName ?? '');
      } catch (err) {
        toast.error(err, 'Failed to load bank details.');
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  // Resolve the account name whenever a bank + a full 10-digit number are set.
  const resolve = useCallback(
    async (acct: string, code: string) => {
      if (acct.length !== 10 || !code) {
        setAccountName('');
        return;
      }
      setResolving(true);
      setResolveError(null);
      try {
        const res = await profileApi.resolveAccount(acct, code);
        setAccountName(res.accountName);
      } catch (err) {
        setAccountName('');
        setResolveError('Could not verify this account. Check the details.');
      } finally {
        setResolving(false);
      }
    },
    [],
  );

  useEffect(() => {
    void resolve(accountNumber, bankCode);
    // Re-run when either input changes.
  }, [accountNumber, bankCode, resolve]);

  async function handleSave() {
    if (!bankCode) return toast.error('Select a bank.');
    if (accountNumber.length !== 10) {
      return toast.error('Account number must be 10 digits.');
    }
    if (!accountName) {
      return toast.error('Confirm the account name before saving.');
    }
    try {
      setBusy(true);
      await profileApi.updatePayoutBank({
        accountNumber: accountNumber.trim(),
        bankCode: bankCode.trim(),
        accountName: accountName.trim(),
      });
      toast.success('Payout bank updated.');
      onSaved();
    } catch (err) {
      toast.error(err, 'Failed to save bank details.');
    } finally {
      setBusy(false);
    }
  }

  const canSave = !!bankCode && accountNumber.length === 10 && !!accountName && !busy;

  // Alphabetical, query-filtered bank list for the searchable dropdown. When the
  // field already shows the selected bank's name, show ALL banks so the user can
  // re-pick without clearing first.
  const bankQuery = bankSearch.trim().toLowerCase();
  const selectedBankName = banks
    .find((b) => b.bankCode === bankCode)
    ?.bankName.toLowerCase();
  const showAllBanks = !bankQuery || (!!bankCode && bankQuery === selectedBankName);
  const filteredBanks = [...banks]
    .sort((a, b) => a.bankName.localeCompare(b.bankName))
    .filter((b) => showAllBanks || b.bankName.toLowerCase().includes(bankQuery));

  return (
    <Modal open onClose={onClose} title="Payout bank account">
      {loading ? (
        <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
          <Spinner />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            All payouts land in this Nigerian bank account. Verify the resolved
            name carefully — PayIQ cannot recover funds sent to the wrong
            account.
          </p>

          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              fontSize: 13,
              color: 'var(--text-secondary)',
            }}
          >
            <span style={{ fontWeight: 500 }}>Bank</span>
            <div style={{ position: 'relative' }}>
              <input
                value={bankSearch}
                placeholder="Search for your bank…"
                onChange={(e) => {
                  setBankSearch(e.target.value);
                  setBankMenuOpen(true);
                  // Typing invalidates the current selection until re-picked.
                  if (bankCode) setBankCode('');
                }}
                onFocus={() => setBankMenuOpen(true)}
                // Delay close so an option's onMouseDown/click registers first.
                onBlur={() => setTimeout(() => setBankMenuOpen(false), 150)}
                style={{
                  width: '100%',
                  height: 42,
                  padding: '0 12px',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-card)',
                  color: 'var(--text-primary)',
                  fontSize: 14,
                  boxSizing: 'border-box',
                }}
              />
              {bankMenuOpen && filteredBanks.length > 0 ? (
                <div
                  style={{
                    position: 'absolute',
                    top: 46,
                    left: 0,
                    right: 0,
                    maxHeight: 220,
                    overflowY: 'auto',
                    zIndex: 20,
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                  }}
                >
                  {filteredBanks.map((b) => (
                    <button
                      key={b.bankCode}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setBankCode(b.bankCode);
                        setBankSearch(b.bankName);
                        setBankMenuOpen(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        fontSize: 14,
                        border: 'none',
                        background:
                          b.bankCode === bankCode
                            ? 'var(--bg-muted)'
                            : 'transparent',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                      }}
                    >
                      {b.bankName}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </label>

          <Input
            label="Account number"
            inputMode="numeric"
            maxLength={10}
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
            placeholder="10-digit NUBAN"
            error={resolveError}
          />

          {/* Resolved name — read-only confirmation. */}
          <div
            style={{
              minHeight: 42,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '0 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-muted)',
              fontSize: 14,
              color: accountName ? 'var(--text-primary)' : 'var(--text-muted)',
            }}
          >
            {resolving ? (
              <>
                <Spinner size={16} /> Verifying account…
              </>
            ) : (
              accountName || 'Account name appears here'
            )}
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8,
            }}
          >
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={handleSave} loading={busy} disabled={!canSave}>
              Save bank details
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
